import {
  BookOpen,
  FileText,
  Heart,
  Shield,
  Users,
  Calendar,
  Download,
  Search,
  Award,
  Sparkles,
  GraduationCap,
  Activity,
  MessageSquare,
} from "lucide-react";
import { useState } from "react";

export default function KnowledgeHubPage() {
  const [activeTab, setActiveTab] = useState<
    "knowledge" | "policy" | "community"
  >("knowledge");

  const knowledgeCategories = [
    {
      icon: <Sparkles className="h-10 w-10" />,
      title: "Jain Values for Student Development",
      count: "25+ Resources",
      color: "from-[#7A1E1E] to-[#8B2E2E]",
      topics: [
        "Ahimsa in daily life",
        "Aparigraha practices",
        "Satya & integrity",
        "Meditation techniques",
      ],
    },
    {
      icon: <Shield className="h-10 w-10" />,
      title: "S-ARDA Governance Principles",
      count: "30+ Guides",
      color: "from-[#D4A017] to-[#B8900F]",
      topics: [
        "Governance frameworks",
        "Compliance guidelines",
        "Transparency protocols",
        "Committee structures",
      ],
    },
    {
      icon: <BookOpen className="h-10 w-10" />,
      title: "Hostel Management Best Practices",
      count: "40+ Articles",
      color: "from-[#7A1E1E] to-[#8B2E2E]",
      topics: [
        "Operations management",
        "Student welfare",
        "Facility maintenance",
        "Staff training",
      ],
    },
    {
      icon: <Heart className="h-10 w-10" />,
      title: "Nutrition & Health Guides",
      count: "35+ Resources",
      color: "from-[#D4A017] to-[#B8900F]",
      topics: [
        "Balanced diet plans",
        "Jain nutrition",
        "Mental health",
        "Fitness programs",
      ],
    },
    {
      icon: <GraduationCap className="h-10 w-10" />,
      title: "Academic Strategy Papers",
      count: "20+ Papers",
      color: "from-[#7A1E1E] to-[#8B2E2E]",
      topics: [
        "Study techniques",
        "Exam preparation",
        "Career planning",
        "Skill development",
      ],
    },
    {
      icon: <Award className="h-10 w-10" />,
      title: "Case Studies",
      count: "15+ Studies",
      color: "from-[#D4A017] to-[#B8900F]",
      topics: [
        "Success stories",
        "Best practices",
        "Impact reports",
        "Transformation journeys",
      ],
    },
  ];

  const policyDocuments = [
    {
      title: "JBF Constitution",
      description: "Official constitution and founding principles",
      icon: <FileText className="h-8 w-8" />,
      pages: "45 pages",
    },
    {
      title: "S-ARDA Alignment for Community Institutions",
      description: "Framework for implementing S-ARDA in community settings",
      icon: <Shield className="h-8 w-8" />,
      pages: "60 pages",
    },
    {
      title: "Code of Ethics",
      description: "Ethical standards for trustees and administrators",
      icon: <Award className="h-8 w-8" />,
      pages: "30 pages",
    },
    {
      title: "Safety & Welfare Protocols",
      description: "Comprehensive safety guidelines and procedures",
      icon: <Shield className="h-8 w-8" />,
      pages: "55 pages",
    },
    {
      title: "Legal Guidelines for Hostels",
      description: "Legal compliance and regulatory framework",
      icon: <FileText className="h-8 w-8" />,
      pages: "40 pages",
    },
  ];

  const communityEvents = [
    {
      title: "National Alumni Meet 2025",
      date: "March 15-17, 2025",
      location: "Mumbai",
      type: "Conference",
      icon: <Users className="h-8 w-8" />,
    },
    {
      title: "Hostel Administrators Workshop",
      date: "April 5-7, 2025",
      location: "Bangalore",
      type: "Workshop",
      icon: <BookOpen className="h-8 w-8" />,
    },
    {
      title: "Student Leadership Summit",
      date: "May 20-22, 2025",
      location: "Delhi",
      type: "Summit",
      icon: <GraduationCap className="h-8 w-8" />,
    },
    {
      title: "Donor Appreciation Yatra",
      date: "June 10-12, 2025",
      location: "Jaipur",
      type: "Yatra",
      icon: <Heart className="h-8 w-8" />,
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative flex min-h-[70vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1582133776712-0b942f3ef601?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxsaWJyYXJ5JTIwa25vd2xlZGdlJTIwYm9va3N8ZW58MXx8fHwxNzY1MTk1OTYzfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
            alt="Library knowledge"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#7A1E1E]/70 to-[#8B2E2E]/60" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 text-white sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-5xl lg:text-6xl">Knowledge Hub</h1>
            <p className="text-2xl leading-relaxed text-gray-100">
              Your comprehensive resource center for Jain values, governance
              excellence, hostel management, and student development.
            </p>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="bg-white py-8 shadow-md">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="relative mx-auto max-w-2xl">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
            <input
              type="text"
              placeholder="Search knowledge base, articles, guides..."
              className="w-full rounded-lg border border-gray-300 py-3 pl-12 pr-4 focus:border-transparent focus:ring-2 focus:ring-[#7A1E1E]"
            />
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="bg-[#FFF9F0] py-4 sm:py-6">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div
            className="scrollbar-hide flex justify-center gap-1.5 overflow-x-auto sm:gap-2"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            <style>{`
              .scrollbar-hide::-webkit-scrollbar { display: none; }
            `}</style>
            <button
              onClick={() => setActiveTab("knowledge")}
              className={`flex-shrink-0 rounded-lg px-4 py-2 text-xs font-medium transition-all sm:px-6 sm:py-2.5 sm:text-sm md:px-8 md:py-3 ${
                activeTab === "knowledge"
                  ? "bg-white text-[#7A1E1E] shadow-sm"
                  : "bg-[#FFF5E6] text-gray-500 hover:bg-white/50"
              }`}
            >
              Knowledge
            </button>
            <button
              onClick={() => setActiveTab("policy")}
              className={`flex-shrink-0 rounded-lg px-4 py-2 text-xs font-medium transition-all sm:px-6 sm:py-2.5 sm:text-sm md:px-8 md:py-3 ${
                activeTab === "policy"
                  ? "bg-white text-[#7A1E1E] shadow-sm"
                  : "bg-[#FFF5E6] text-gray-500 hover:bg-white/50"
              }`}
            >
              Policy
            </button>
            <button
              onClick={() => setActiveTab("community")}
              className={`flex-shrink-0 rounded-lg px-4 py-2 text-xs font-medium transition-all sm:px-6 sm:py-2.5 sm:text-sm md:px-8 md:py-3 ${
                activeTab === "community"
                  ? "bg-white text-[#7A1E1E] shadow-sm"
                  : "bg-[#FFF5E6] text-gray-500 hover:bg-white/50"
              }`}
            >
              Community
            </button>
          </div>
        </div>
      </section>

      {/* Knowledge Repository Tab */}
      {activeTab === "knowledge" && (
        <section className="bg-[#FFF9F0] py-16 lg:py-24">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-3xl text-[#7A1E1E] sm:text-4xl">
                Knowledge Repository
              </h2>
              <div className="mx-auto mb-6 h-1 w-24 bg-[#D4A017]" />
              <p className="mx-auto max-w-3xl text-lg text-gray-700">
                Curated resources across key areas of hostel development and
                student welfare.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {knowledgeCategories.map(category => (
                <div
                  key={category.title}
                  className="overflow-hidden rounded-xl bg-white shadow-lg transition-all hover:shadow-2xl"
                >
                  <div
                    className={`bg-gradient-to-br ${category.color} p-6 text-white`}
                  >
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                      {category.icon}
                    </div>
                    <h3 className="mb-2 text-center text-xl">
                      {category.title}
                    </h3>
                    <p className="text-center text-sm text-gray-200">
                      {category.count}
                    </p>
                  </div>
                  <div className="p-6">
                    <h4 className="mb-3 text-sm text-gray-600">
                      Topics include:
                    </h4>
                    <ul className="space-y-2">
                      {category.topics.map(topic => (
                        <li
                          key={topic}
                          className="flex items-center gap-2 text-sm text-gray-700"
                        >
                          <div className="h-1.5 w-1.5 rounded-full bg-[#D4A017]" />
                          {topic}
                        </li>
                      ))}
                    </ul>
                    <button className="mt-6 w-full rounded-lg bg-[#7A1E1E] py-2 text-sm text-white transition-all hover:bg-[#5A1515]">
                      Browse Resources
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Policy Documents Tab */}
      {activeTab === "policy" && (
        <section className="bg-[#FFF5E6] py-16 lg:py-24">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-3xl text-[#7A1E1E] sm:text-4xl">
                Policy Documents
              </h2>
              <div className="mx-auto mb-6 h-1 w-24 bg-[#D4A017]" />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {policyDocuments.map(doc => (
                <div
                  key={doc.title}
                  className="flex items-start gap-4 rounded-xl bg-white p-6 shadow-md transition-all hover:shadow-xl"
                >
                  <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white">
                    {doc.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-2 text-lg text-[#7A1E1E]">{doc.title}</h3>
                    <p className="mb-2 text-sm text-gray-700">
                      {doc.description}
                    </p>
                    <p className="mb-3 text-xs text-gray-500">{doc.pages}</p>
                    <button className="flex items-center gap-2 rounded-lg bg-[#D4A017] px-4 py-2 text-sm text-white transition-all hover:bg-[#B8900F]">
                      <Download className="h-4 w-4" />
                      Download PDF
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Community Engagement Tab */}
      {activeTab === "community" && (
        <section className="bg-white py-16 lg:py-24">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-3xl text-[#7A1E1E] sm:text-4xl">
                Community Engagement
              </h2>
              <p className="mx-auto max-w-3xl text-lg text-gray-600">
                Connect, participate, and grow with the JBF community
              </p>
            </div>

            <div className="mb-16 grid grid-cols-1 gap-8 md:grid-cols-3">
              {[
                {
                  title: "Alumni meet-ups",
                  description: "Connect with fellow alumni",
                  icon: <Users className="h-10 w-10" />,
                },
                {
                  title: "Parent forums",
                  description: "Engage with parent community",
                  icon: <MessageSquare className="h-10 w-10" />,
                },
                {
                  title: "JBF events & yatras",
                  description: "Cultural and spiritual events",
                  icon: <Calendar className="h-10 w-10" />,
                },
              ].map(item => (
                <div
                  key={item.title}
                  className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-md transition-all hover:shadow-lg"
                >
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF5E6] text-[#D4A017]">
                    {item.icon}
                  </div>
                  <h3 className="mb-2 text-lg text-[#7A1E1E]">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-200 pt-12">
              <h3 className="mb-8 text-center text-2xl text-[#7A1E1E]">
                Upcoming Events
              </h3>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                {communityEvents.map(event => (
                  <div
                    key={event.title}
                    className="rounded-xl border-l-4 border-[#D4A017] bg-gradient-to-br from-[#FFF9F0] to-[#FFF5E6] p-6 shadow-lg transition-all hover:shadow-xl"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white">
                        {event.icon}
                      </div>
                      <div className="flex-1">
                        <div className="mb-2 inline-block rounded-full bg-[#D4A017] px-3 py-1 text-xs text-white">
                          {event.type}
                        </div>
                        <h3 className="mb-2 text-lg text-[#7A1E1E]">
                          {event.title}
                        </h3>
                        <div className="mb-1 flex items-center gap-2 text-sm text-gray-700">
                          <Calendar className="h-4 w-4 text-[#D4A017]" />
                          {event.date}
                        </div>
                        <div className="mb-4 flex items-center gap-2 text-sm text-gray-700">
                          <Activity className="h-4 w-4 text-[#D4A017]" />
                          {event.location}
                        </div>
                        <button className="rounded-lg bg-[#7A1E1E] px-4 py-2 text-sm text-white transition-all hover:bg-[#5A1515]">
                          Register Now
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
